import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { COLORS } from '@src/constants/design-tokens/colors';
import { memo, type ReactElement, useEffect, useRef, useState } from 'react';

/**
 * 進捗表示用のコンパクトな円グラフコンポーネントのProps型定義
 */
type ProgressCircleProps = {
  value: number;
  size?: number;
  thickness?: number;
  showPercentage?: boolean;
  variant?: 'determinate' | 'indeterminate';
  _color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
};

const PRIMARY_MAIN = COLORS.primary.main;

/**
 * 進捗表示用のコンパクトな円グラフコンポーネント
 *
 * 入力進捗を円グラフとパーセンテージで表示します。
 * 操作エリア内にコンパクトに配置することを想定しています。
 *
 * @param {ProgressCircleProps} props - コンポーネントのプロパティ
 * @param {number} props.value - 進捗値（0-100）
 * @param {number} [props.size=40] - 円グラフのサイズ
 * @param {number} [props.thickness=4] - 円グラフの線の太さ
 * @param {boolean} [props.showPercentage=true] - パーセンテージ表示の有無
 * @param {'determinate' | 'indeterminate'} [props.variant='determinate'] - 進捗の種類
 * @param {'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'} [props.color='primary'] - 色
 * @returns {ReactElement} 進捗表示用の円グラフコンポーネント
 *
 * @example
 * ```tsx
 * <ProgressCircle value={75} size={32} showPercentage={false} />
 * ```
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
const ProgressCircle = memo(
  ({
    value,
    size = 40,
    thickness = 4,
    showPercentage = true,
    variant = 'determinate',
  }: ProgressCircleProps): ReactElement => {
    const [isAnimating, setIsAnimating] = useState(false);
    const previousValueRef = useRef(value);
    const glowShadow = (blur: number, opacity: number) =>
      `drop-shadow(0 0 ${blur}px ${alpha(PRIMARY_MAIN, opacity)})`;

    useEffect(() => {
      const previousValue = previousValueRef.current;

      if (value > previousValue) {
        previousValueRef.current = value;
        setIsAnimating(true);
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 1000);
        return () => clearTimeout(timer);
      }

      if (value !== previousValue) {
        previousValueRef.current = value;
        setIsAnimating(false);
      }
    }, [value]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          // グローエフェクト用の余白を追加
          padding: '4px',
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant={variant}
            value={variant === 'determinate' ? value : undefined}
            size={size}
            thickness={thickness}
            color="primary"
            sx={{
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
                transition: 'stroke-dasharray 0.5s ease-in-out',
                stroke: PRIMARY_MAIN, // 常にブランドカラー
              },
              // 進捗増加時のアニメーション効果
              ...(isAnimating && {
                '& .MuiCircularProgress-circle': {
                  filter: glowShadow(12, 0.8),
                  animation: 'glow 0.8s ease-in-out',
                },
              }),
              '@keyframes glow': {
                '0%': {
                  filter: glowShadow(4, 0.4),
                },
                '50%': {
                  filter: `${glowShadow(16, 1)} ${glowShadow(24, 0.6)}`,
                },
                '100%': {
                  filter: glowShadow(4, 0.4),
                },
              },
            }}
          />
          {variant === 'determinate' && showPercentage && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{
                  fontSize: size * 0.25,
                  fontWeight: 600,
                  color: 'text.secondary',
                  lineHeight: 1,
                  transition: 'all 0.3s ease',
                  // 進捗増加時のテキストエフェクト
                  ...(isAnimating && {
                    color: PRIMARY_MAIN,
                    fontWeight: 700,
                    textShadow: `0 0 8px ${alpha(PRIMARY_MAIN, 0.8)}`,
                    animation: 'textGlow 0.8s ease-in-out',
                  }),
                  '@keyframes textGlow': {
                    '0%': {
                      textShadow: `0 0 2px ${alpha(PRIMARY_MAIN, 0.3)}`,
                    },
                    '50%': {
                      textShadow: `0 0 12px ${alpha(PRIMARY_MAIN, 1)}, 0 0 16px ${alpha(PRIMARY_MAIN, 0.6)}`,
                    },
                    '100%': {
                      textShadow: `0 0 2px ${alpha(PRIMARY_MAIN, 0.3)}`,
                    },
                  },
                }}
              >
                {Math.round(value)}%
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

ProgressCircle.displayName = 'ProgressCircle';

export default ProgressCircle;
